import { type PropsWithChildren, type ReactElement, useState } from 'react';
import classNames from 'classnames';

export interface EditPanelProps {
    open?: boolean;
    preview: ReactElement;
    className?: string;
    openOnDesktop?: boolean;
}

type EditPanelExpandCallbackProps = EditPanelProps & { expanded: boolean; setExpanded: (open: boolean) => void };

export const EditPanel = ({
    open = false,
    preview,
    openOnDesktop = false,
    className = '',
    children,
}: PropsWithChildren<EditPanelProps>) => {
    const [expanded, setExpanded] = useState(open);
    return (
        <EditPanelExpandCallback
            expanded={expanded}
            setExpanded={setExpanded}
            preview={preview}
            openOnDesktop={openOnDesktop}
            className={className}>
            {children}
        </EditPanelExpandCallback>
    );
};

export const EditPanelExpandCallback = ({
    expanded,
    setExpanded,
    preview,
    openOnDesktop = false,
    className = '',
    children,
}: PropsWithChildren<EditPanelExpandCallbackProps>) => {
    const topBarClassName = openOnDesktop ? 'u-hide-gt768' : '';
    const contentClassName = openOnDesktop ? 'u-hide-lt768' : 'u-display-none';
    const onPanelClick = expanded ? () => {} : () => setExpanded(!expanded);
    return (
        <div
            id="editPanel"
            data-testid="editPanel"
            className={classNames(`panel panel--neutral edit-panel ${className}`, { expanded: expanded, 'open-on-desktop': openOnDesktop })}
            onClick={onPanelClick}>
            <div className={topBarClassName} data-testid="editPanel-header">
                <button
                    type="button"
                    className={classNames('edit-panel-button', { 'button--close': expanded, 'button--edit': !expanded })}
                    data-testid="editPanel-button"
                    aria-label={expanded ? 'lukk redigering' : 'åpne redigering'}
                    onClick={(): void => setExpanded(!expanded)}
                />
                {!expanded && preview}
            </div>
            <div className={classNames('', { [contentClassName]: !expanded, 'u-pt16': !openOnDesktop })} data-testid="editPanel-content">
                {children}
            </div>
        </div>
    );
};
